/**
 * The desktop host, and the only file that knows Tauri exists.
 *
 * Everything Tauri is behind a dynamic `import()`. That is not style — it is the
 * constraint the whole packaging job hangs on. `npm run dev` and
 * `npm run shots` drive the real game in a real browser and are this project's
 * only way of looking at its own art; a static import would pull the desktop
 * IPC bridge into the browser bundle, where `window.__TAURI_INTERNALS__` does
 * not exist. Behind a dynamic import it becomes a separate chunk that a browser
 * never fetches, and the browser build is byte-for-byte the game it was.
 *
 * So: `isDesktop()` is a cheap synchronous look at one global, and nothing else
 * here runs unless it is true.
 */

import { chooseSaveDir, joinPath, type SaveFs } from './savestore.ts';

/** True inside the packaged desktop shell, false in any browser. */
export function isDesktop(): boolean {
  return typeof window !== 'undefined' && '__TAURI_INTERNALS__' in window;
}

/**
 * The filesystem, expressed as the six operations `savestore.ts` asks for.
 *
 * `remove` is allowed to fail: the callers use it to clear things that may not
 * be there, and the plugin throws rather than shrugging when a path is missing.
 */
async function desktopFs(): Promise<SaveFs> {
  const fs = await import('@tauri-apps/plugin-fs');
  return {
    mkdirp: async (dir) => {
      await fs.mkdir(dir, { recursive: true });
    },
    exists: (path) => fs.exists(path),
    readText: (path) => fs.readTextFile(path),
    writeText: (path, text) => fs.writeTextFile(path, text),
    remove: (path) => fs.remove(path),
    rename: (from, to) => fs.rename(from, to),
  };
}

export interface DesktopSaveDir {
  fs: SaveFs;
  dir: string;
  /** How to describe it to a player: the portable folder, or the fallback. */
  location: string;
  portable: boolean;
}

/**
 * Find somewhere to keep saves, portable first.
 *
 * On Windows `resourceDir()` is the folder the executable sits in, which is
 * exactly what the owner asked for: unzip a folder, run the exe, and the saves
 * are right there next to it where they can be copied, backed up or deleted
 * without going hunting through `AppData`.
 *
 * The fallback matters more than it looks. Nothing stops a player dropping the
 * folder into `C:\Program Files`, and that folder is not writable without
 * administrator rights. Without a fallback the game would come up, play
 * perfectly, and quietly fail to save every time — the single worst outcome
 * available. So the portable folder is *proved* by writing a file into it and
 * reading it back, and when that fails the save goes to the user's app-data
 * directory instead, and the game says so on the menu.
 */
export async function desktopSaveDir(): Promise<DesktopSaveDir | null> {
  try {
    const fs = await desktopFs();
    const path = await import('@tauri-apps/api/path');
    const beside = joinPath(await path.resourceDir(), 'saves');
    const appdata = joinPath(await path.appLocalDataDir(), 'saves');
    const choice = await chooseSaveDir(fs, [beside, appdata]);
    if (!choice) return null;
    const portable = choice.index === 0;
    return {
      fs,
      dir: choice.dir,
      location: portable ? `.\\saves (beside the game)` : choice.dir,
      portable,
    };
  } catch {
    // A desktop shell we could not talk to is a browser as far as saving goes.
    return null;
  }
}
